/**
 * TelefoonTab — alle telefoongesprekken van één klant op de klantpagina.
 *
 * Haalt /api/telephony/history?customerId=<sfId> op en toont de gesprekken;
 * klik op een gesprek opent het bestaande CallDetailPanel (opname-speler,
 * AI-samenvatting, transcript) inline eronder.
 */

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useCallDrawerStore } from '../../stores/call-drawer-store';

interface CallRow {
  id: string;
  direction: 'inbound' | 'outbound';
  fromNumber: string | null;
  toNumber: string | null;
  status: string;
  startedAt: string;
  answeredAt: string | null;
  durationSeconds: number | null;
  recordingUrl: string | null;
  aiTopic: string | null;
  aiSentiment: 'positief' | 'neutraal' | 'negatief' | null;
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('nl-NL', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function formatDuration(seconds: number | null): string {
  if (!seconds || seconds <= 0) return '-';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

const STATUS_BADGE: Record<string, { label: string; cls: string }> = {
  completed: { label: 'Beantwoord', cls: 'bg-emerald-50 text-emerald-700' },
  voicemail: { label: 'Voicemail', cls: 'bg-purple-50 text-purple-700' },
  missed: { label: 'Gemist', cls: 'bg-red-50 text-red-700' },
  forwarded: { label: 'Doorgeschakeld', cls: 'bg-sky-50 text-sky-700' },
  rejected: { label: 'Geweigerd', cls: 'bg-amber-50 text-amber-700' },
  failed: { label: 'Mislukt', cls: 'bg-red-50 text-red-700' },
  active: { label: 'Actief', cls: 'bg-indigo-50 text-indigo-700' },
  ringing: { label: 'Rinkelt', cls: 'bg-slate-50 text-slate-600' },
};

const SENTIMENT_CLS: Record<string, string> = {
  positief: 'bg-green-100 text-green-800',
  neutraal: 'bg-slate-100 text-slate-700',
  negatief: 'bg-red-100 text-red-800',
};

export function TelefoonTab({ customerId }: { customerId: string }) {
  const [calls, setCalls] = useState<CallRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const openCallId = useCallDrawerStore((s) => s.openCallId);
  const openCall = useCallDrawerStore((s) => s.open);

  const fetchCalls = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) return;
      const res = await fetch(
        `/api/telephony/history?customerId=${encodeURIComponent(customerId)}&limit=100`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setCalls(data.calls ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Laden mislukt');
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => { void fetchCalls(); }, [fetchCalls]);

  if (loading) {
    return <div className="p-6 text-center text-sm text-slate-400">Gesprekken laden…</div>;
  }
  if (error) {
    return <div className="p-6 text-center text-sm text-red-500">{error}</div>;
  }
  if (calls.length === 0) {
    return <div className="p-6 text-center text-sm text-slate-400">Geen telefoongesprekken voor deze klant.</div>;
  }

  return (
    <div className="space-y-3">
      <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100">
        {calls.map((c) => {
          // Uitgaand + nooit opgenomen wordt als 'completed' gefinaliseerd;
          // zonder dit onderscheid toont dat groen als "Beantwoord".
          const badge =
            c.status === 'completed' && c.direction === 'outbound' && !c.answeredAt
              ? { label: 'Geen gehoor', cls: 'bg-slate-100 text-slate-600' }
              : STATUS_BADGE[c.status] ?? { label: c.status, cls: 'bg-slate-50 text-slate-600' };
          const isSelected = c.id === openCallId;
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => openCall(c.id)}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm hover:bg-slate-50 ${
                isSelected ? 'bg-indigo-50' : ''
              }`}
            >
              <span
                className="text-slate-400 shrink-0"
                title={c.direction === 'inbound' ? 'Inkomend' : 'Uitgaand'}
              >
                {c.direction === 'inbound' ? '↓' : '↑'}
              </span>
              <span className="text-slate-600 whitespace-nowrap w-32 shrink-0">{formatDateTime(c.startedAt)}</span>
              <span className={`px-2 py-0.5 rounded text-xs font-medium shrink-0 ${badge.cls}`}>{badge.label}</span>
              {c.aiTopic && (
                <span className="px-2 py-0.5 rounded-full text-xs bg-indigo-100 text-indigo-700 truncate max-w-[220px]">
                  {c.aiTopic}
                </span>
              )}
              {c.aiSentiment && (
                <span className={`px-2 py-0.5 rounded-full text-xs shrink-0 ${SENTIMENT_CLS[c.aiSentiment] ?? SENTIMENT_CLS.neutraal}`}>
                  {c.aiSentiment}
                </span>
              )}
              <span className="ml-auto text-xs font-mono text-slate-500 shrink-0">{formatDuration(c.durationSeconds)}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
